import { clsx } from 'clsx'
import { type FC, type MouseEvent, useState } from 'react'

import { IconPlayIconOnly } from '@redteclab/icons'

import poster from '../../../../images/video-title-screen.jpg'

import { ProductCarouselGalleryImage } from './ProductCarouselGalleryImage'
import { ProductCarouselGalleryItem } from './ProductCarouselGalleryItem'
import { type ProductCarouselGalleryVideoItemProps } from './ProductCarouselGalleryVideoItemProps'

const ProductCarouselGalleryVideoItem: FC<
  ProductCarouselGalleryVideoItemProps
> = ({
  index,
  lazy = false,
  onClick,
  onScrolledInView,
  playOnClick = false,
  playOnScrolledIntoView = false,
  productName,
  useLargeSizes,
  video,
  zoomEnabled,
}) => {
  const [showVideo, setShowVideo] = useState(false)

  const mp4Src = video.mp4 ?? video.mp4_mobile
  const webmSrc = video.webm ?? video.webm_mobile

  const handleOnClick = (): void => {
    if (playOnClick) {
      setShowVideo(true)
    } else {
      onClick()
    }
  }

  const handleVideoWrapperOnClick = (event: MouseEvent): void => {
    event.stopPropagation()
    setShowVideo(true)
  }

  const handleScrolledIntoView = (): void => {
    if (playOnScrolledIntoView) {
      setShowVideo(true)
    }
    onScrolledInView()
  }

  return (
    <ProductCarouselGalleryItem
      index={index}
      onClick={handleOnClick}
      onScrolledInView={handleScrolledIntoView}
      zoomEnabled={zoomEnabled}
    >
      <div
        /**
         * We disable pointer events for small screens on the parent (ProductCarouselGalleryItem)
         * because we don't want to trigger the event computations for the zoomed image, which
         * is only shown starting with "tablet:".
         *
         * This means that on smaller screens we are not able to detect when the user taps on the video
         * and therefore cannot display/start it.
         *
         * The idea here is to "revert" the pointer events settings from the parent such that
         *  - on small screens we can display the video when the user clicks
         *  - on larger screens we look if we need to allow the standard parent behavior (notify click) or
         *    show the video (large gallery)
         */
        className={clsx(
          'pointer-events-auto flex size-full items-center justify-center',
          !playOnClick && 'tablet:pointer-events-none',
        )}
        onClick={handleVideoWrapperOnClick}
        role="presentation"
      >
        {showVideo ? (
          <video autoPlay className="w-full" controls poster={poster.src}>
            <source src={mp4Src} type="video/mp4" />
            <source src={webmSrc} type="video/webm" />
          </video>
        ) : (
          <div className="h-full" role="button">
            {video.thumbnail ? (
              <ProductCarouselGalleryImage
                alt={productName}
                lazy={lazy}
                source={video.thumbnail}
                useLargeSizes={useLargeSizes}
              />
            ) : null}
            <div
              className={clsx(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'before:absolute before:size-full before:-translate-x-1/2 before:rounded-full before:bg-dark-brand before:opacity-20',
              )}
            >
              <IconPlayIconOnly className="size-20 fill-dark-brand" />
            </div>
          </div>
        )}
      </div>
    </ProductCarouselGalleryItem>
  )
}

export { ProductCarouselGalleryVideoItem }
